import * as Sentry from '@sentry/react'
import type FamilyPortalAPIClient from '../../auth/FamilyPortalAPIClient'
import type {
  HrcConnectCompleteRequest,
  HrcConnectCompleteResponse,
  HrcConnectStart,
  HrcConnectSubject,
  HrcConnectedSystem,
  HrcPollState,
} from '../types'

const START_PATH = '/hrc/connect/start'
const COMPLETE_PATH = '/hrc/connect/complete'
const STATUS_PATH = '/hrc/connect/status'
const SUBJECTS_PATH = '/hrc/connect/subjects'

export type HrcApiErrorKind =
  /** fetch never got a response. */
  | 'network'
  /** 5xx, UPSTREAM_UNKNOWN, or any envelope whose outcome is unknown: the server may have saved it. */
  | 'server'
  /** RATE_LIMITED, 408 / 425 / 429: transient by definition. */
  | 'rate-limited'
  /** 401 with no envelope, including the API client's synthetic 401 when the token getter fails transiently. */
  | 'unauthorized'
  /** UNAUTHENTICATED envelope: the BFF says the session is gone. Needs its own honest state. */
  | 'session'
  /** ENROLL_IN_PROGRESS / outcome in_progress: the first request is still being saved. */
  | 'in-progress'
  /** A 2xx whose body does not prove `status === 'ACTIVE'`, or VENDOR_UNCONFIRMED. The server may have committed. */
  | 'unconfirmed'
  /** HANDOFF_EXPIRED / 410: the handoff is terminal. */
  | 'expired'
  /** HANDOFF_CONFLICT / bare 409. The FLOW (not this client) decides whether a replay downgrades it to retryable. */
  | 'conflict'
  /** 403: verification mismatch; no row exists. */
  | 'forbidden'
  /** CONNECTION_REVOKED: a previously shared connection was revoked. Normal and recoverable: reconnect via Start over. */
  | 'revoked'
  /** VENDOR_ERROR / VENDOR_UNCONFIRMED with outcome failed: the EHR vendor is unavailable or returned bad data; nothing was written. */
  | 'vendor'
  /** INVALID_REQUEST / 400 / 422: the request itself is wrong; re-sending it cannot help. */
  | 'invalid'
  /** INTERNAL_ERROR with outcome failed: not retryable with the same key. */
  | 'internal'
  | 'unknown'

const RETRYABLE_KINDS: ReadonlySet<HrcApiErrorKind> = new Set([
  'network',
  'server',
  'rate-limited',
  'unauthorized',
  'session',
  'vendor',
  'in-progress',
  'unconfirmed',
])

/**
 * Error with a stable, user-safe message. Server message text is deliberately never surfaced; callers
 * branch on `kind` / `retryable` instead. `retryable` means the SAME request (same Idempotency-Key) may be
 * re-sent: the request is idempotent, so re-sending is always safe, whereas discarding it could strand a
 * connection the server already saved.
 */
export class HrcApiError extends Error {
  readonly kind: HrcApiErrorKind
  readonly status: number | null
  readonly retryable: boolean
  /** The BFF's stable machine code (e.g. ENROLL_IN_PROGRESS), when the error body carried one. */
  readonly code: string | undefined
  /** Seconds the server asked us to wait, from the BODY (an integer); null when absent or not a valid integer. */
  readonly retryAfterSeconds: number | null

  constructor(
    kind: HrcApiErrorKind,
    status: number | null,
    message: string,
    code?: string,
    retryAfterSeconds: number | null = null
  ) {
    super(message)
    this.name = 'HrcApiError'
    this.kind = kind
    this.status = status
    this.retryable = RETRYABLE_KINDS.has(kind)
    this.code = code
    this.retryAfterSeconds = retryAfterSeconds
  }
}

/**
 * The error envelope (CONTRACT_V4 R12), emitted at the TOP LEVEL of the body:
 * `{ code, outcome, retryable, retryAfterSeconds, message }`. The prod SPA is cross-origin, so the browser hides
 * most response headers: classification uses this body and the status code ONLY, never a header.
 */
type Outcome = 'failed' | 'unknown' | 'in_progress'

type ErrorEnvelope = {
  code?: string
  outcome?: Outcome
  retryable?: boolean
  retryAfterSeconds: number | null
}

const MAX_RETRY_AFTER_SECONDS = 3600

function readEnvelope(value: Record<string, unknown>): ErrorEnvelope | null {
  const code = typeof value.code === 'string' && value.code !== '' ? value.code : undefined
  const outcome =
    value.outcome === 'failed' || value.outcome === 'unknown' || value.outcome === 'in_progress'
      ? value.outcome
      : undefined
  if (code === undefined && outcome === undefined) return null
  const seconds = value.retryAfterSeconds
  return {
    code,
    outcome,
    retryable: typeof value.retryable === 'boolean' ? value.retryable : undefined,
    retryAfterSeconds:
      typeof seconds === 'number' && Number.isInteger(seconds) && seconds >= 0
        ? Math.min(seconds, MAX_RETRY_AFTER_SECONDS)
        : null,
  }
}

/** Top-level envelope first; a tolerant fallback to FastAPI's nested `{detail: envelope}`; else none (status only). */
function parseEnvelope(value: unknown): ErrorEnvelope | null {
  if (!isRecord(value)) return null
  return readEnvelope(value) ?? (isRecord(value.detail) ? readEnvelope(value.detail) : null)
}

function kindForStatus(status: number): HrcApiErrorKind {
  if (status === 410) return 'expired'
  if (status === 409) return 'conflict'
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'forbidden'
  if (status === 408 || status === 425 || status === 429) return 'rate-limited'
  if (status === 400 || status === 422) return 'invalid'
  if (status >= 500) return 'server'
  return 'unknown'
}

/**
 * Envelope first (R13: its `code` and `outcome` are authoritative and never upgraded), status only as a fallback.
 * 1. outcome unknown / in_progress is NEVER a hard failure: whatever the code, the request may have been saved, so
 *    it stays retryable with the same key and Start over is never offered.
 * 2. Otherwise the code decides. RATE_LIMITED is retryable, INVALID_REQUEST / INTERNAL_ERROR are not.
 */
function classify(status: number, env: ErrorEnvelope | null): HrcApiErrorKind {
  if (!env) return kindForStatus(status)

  if (env.outcome === 'in_progress' || env.code === 'ENROLL_IN_PROGRESS') return 'in-progress'
  if (env.outcome === 'unknown') {
    if (env.code === 'VENDOR_UNCONFIRMED') return 'unconfirmed'
    if (env.code === 'RATE_LIMITED') return 'rate-limited'
    if (env.code === 'UNAUTHENTICATED') return 'session'
    return 'server'
  }

  switch (env.code) {
    case 'HANDOFF_EXPIRED':
      return 'expired'
    case 'HANDOFF_CONFLICT':
      return 'conflict'
    case 'CONNECTION_REVOKED':
      return 'revoked'
    case 'VENDOR_UNCONFIRMED':
    case 'VENDOR_ERROR':
      return 'vendor' // failed + retryable: nothing was written (V5 R22)
    case 'UPSTREAM_UNKNOWN':
      return 'server'
    case 'RATE_LIMITED':
      return 'rate-limited'
    case 'UNAUTHENTICATED':
      return 'session'
    case 'INVALID_REQUEST':
      return 'invalid'
    case 'INTERNAL_ERROR':
      return 'internal'
  }
  // Unrecognized code: use the envelope's own retryable flag, then the status.
  if (env.retryable === true) return 'server'
  const byStatus = kindForStatus(status)
  if (env.outcome === 'failed' && env.retryable === false && RETRYABLE_KINDS.has(byStatus)) return 'unknown'
  return byStatus
}

async function send(request: () => Promise<Response>): Promise<Response> {
  let res: Response
  try {
    res = await request()
  } catch {
    throw new HrcApiError('network', null, 'Network request failed')
  }
  if (!res.ok) {
    const env = parseEnvelope(await readJsonOrNull(res))
    throw new HrcApiError(
      classify(res.status, env),
      res.status,
      `Request failed (status ${res.status})`,
      env?.code,
      env?.retryAfterSeconds ?? null
    )
  }
  return res
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v !== ''

async function readJsonOrNull(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return null
  }
}

export async function startConnect(
  client: FamilyPortalAPIClient,
  recordSubjectPrincipalId: string
): Promise<HrcConnectStart> {
  // Trailing `true` is suppressUnauthorizedRedirect: this hook renders the 401 itself
  // rather than letting the client log the user out mid-flow. A dead session still
  // logs out via the client's reauth-required branch.
  const res = await send(() => client.post(START_PATH, { recordSubjectPrincipalId }, undefined, true, undefined, true))
  const body = await readJsonOrNull(res)
  if (
    !isRecord(body) ||
    !isNonEmptyString(body.handoff) ||
    !isNonEmptyString(body.handoffNonce) ||
    !isNonEmptyString(body.publicId) ||
    !isNonEmptyString(body.expiresAt)
  ) {
    throw new HrcApiError('invalid', res.status, 'Malformed response')
  }
  return {
    handoff: body.handoff,
    handoffNonce: body.handoffNonce,
    publicId: body.publicId,
    expiresAt: body.expiresAt,
  }
}

const POLL_STATES: ReadonlySet<string> = new Set(['WAITING', 'READY', 'CONNECTED', 'GONE', 'UNKNOWN'])
const SUBJECT_ROLES: ReadonlySet<string> = new Set(['SELF', 'PROBAND'])

const CONNECTED_STATES: ReadonlySet<string> = new Set(['ACTIVE', 'EXPIRED'])

/**
 * Lenient where `toSubject` is strict, and deliberately so: a subject we cannot read is
 * dropped because hiding a person is worse than failing, but this list is what someone
 * ALREADY connected. Losing a tab over an unreadable row in it would trade a real
 * capability for a cosmetic one.
 */
function toHealthSystem(value: unknown): HrcConnectedSystem | null {
  if (!isRecord(value)) return null
  const { name, platformType, state, connectedAt, expiresAt, verificationFailed } = value
  if (typeof state !== 'string' || !CONNECTED_STATES.has(state)) return null
  return {
    ...(isNonEmptyString(name) ? { name } : {}),
    ...(isNonEmptyString(platformType) ? { platformType } : {}),
    state: state as HrcConnectedSystem['state'],
    ...(isNonEmptyString(connectedAt) ? { connectedAt } : {}),
    ...(isNonEmptyString(expiresAt) ? { expiresAt } : {}),
    // Only an explicit `true`: an older BFF omits it, and anything else must never make a
    // working connection look failed.
    ...(verificationFailed === true ? { verificationFailed: true } : {}),
  }
}

function toSubject(value: unknown): HrcConnectSubject | null {
  if (!isRecord(value)) return null
  const { recordSubjectPrincipalId: id, role, name, connections, soonestExpiresAt, healthSystems } = value
  if (!isNonEmptyString(id) || typeof role !== 'string' || !SUBJECT_ROLES.has(role)) return null
  const systems = Array.isArray(healthSystems)
    ? healthSystems.map(toHealthSystem).filter((s): s is HrcConnectedSystem => s !== null)
    : []
  return {
    recordSubjectPrincipalId: id,
    role: role as HrcConnectSubject['role'],
    ...(isNonEmptyString(name) ? { name } : {}),
    connections: typeof connections === 'number' && connections > 0 ? connections : 0,
    ...(isNonEmptyString(soonestExpiresAt) ? { soonestExpiresAt } : {}),
    healthSystems: systems,
  }
}

/**
 * Who this guardian may connect records for. Strict: an entry we cannot read fails the
 * whole response, as does a response we cannot read at all. Returning a short list quietly
 * would hide a subject.
 *
 * An empty list is the server's answer, not a parse failure: the user has not accepted
 * Health Records Access for anyone.
 */
export async function fetchConnectSubjects(client: FamilyPortalAPIClient): Promise<HrcConnectSubject[]> {
  const res = await send(() => client.get(SUBJECTS_PATH, undefined, true))
  const body = await readJsonOrNull(res)
  if (!isRecord(body) || !Array.isArray(body.subjects)) {
    throw new HrcApiError('invalid', res.status, 'Malformed response')
  }
  const subjects = body.subjects.map(toSubject).filter((s): s is HrcConnectSubject => s !== null)
  if (subjects.length !== body.subjects.length) {
    throw new HrcApiError('invalid', res.status, 'Malformed response')
  }
  return subjects
}

/**
 * Where the enrollment has got to, so the flow can send ONE /complete instead of re-sending it on a
 * schedule. Cheap by design: the server reads a row and answers an enum, with no vendor call.
 *
 * Anything we cannot read as a known state is `UNKNOWN` rather than an error. The flow polls this
 * every few seconds, so a strict parse would turn a newer server, a proxy's error page, or a blip
 * into a failure the family user sees — for a request whose whole point is to be unremarkable.
 * A non-2xx still throws: a dead session and a malformed request are bugs, not states.
 */
export async function pollConnectStatus(client: FamilyPortalAPIClient, handoff: string): Promise<HrcPollState> {
  // Trailing `true` is suppressUnauthorizedRedirect, as on the other two calls: a logout redirect
  // mid-wait would destroy the pendingRef holding the Idempotency-Key for a request that may
  // already have committed.
  const res = await send(() => client.post(STATUS_PATH, { handoff }, undefined, true, undefined, true))
  const body = await readJsonOrNull(res)
  return isRecord(body) && typeof body.state === 'string' && POLL_STATES.has(body.state)
    ? (body.state as HrcPollState)
    : 'UNKNOWN'
}

/**
 * A 2xx means the server ACCEPTED the request and may have committed the row, so an unreadable or
 * non-ACTIVE body is "unconfirmed" (retryable with the same key; a same-key replay returns the original
 * result), never a failure that discards the pending request and never a success.
 */
export async function completeConnect(
  client: FamilyPortalAPIClient,
  request: HrcConnectCompleteRequest,
  idempotencyKey: string
): Promise<HrcConnectCompleteResponse> {
  // Trailing `true` is suppressUnauthorizedRedirect. Critical here: a logout redirect
  // would destroy the in-memory pendingRef holding this Idempotency-Key, so a request
  // that may already have committed server-side could never be retried or confirmed.
  const res = await send(() =>
    client.post(COMPLETE_PATH, request, { 'Idempotency-Key': idempotencyKey }, true, undefined, true)
  )
  const body = await readJsonOrNull(res)
  if (!isRecord(body) || body.status !== 'ACTIVE') {
    // Status code only: never the body, ids, or any payload.
    Sentry.captureMessage('hrc.connect.complete returned a 2xx that could not be verified as ACTIVE', {
      level: 'warning',
      tags: { operation: 'hrc.connect.complete', http_status: String(res.status) },
    })
    throw new HrcApiError('unconfirmed', res.status, 'Unconfirmed response')
  }
  return {
    status: 'ACTIVE',
    ...(isNonEmptyString(body.connectionId) ? { connectionId: body.connectionId } : {}),
  }
}
